import {Component, OnInit} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {PageComponent} from '../../../../../core/shared/mvs-page/page.component';
import {MvsCoreService} from '../../../../../core/shared/service/mvs-core.service';
import {MvsObjectNavigationService} from '../../../../../core/shared/navigation/mvs-object-navigation.service';
import {MvsObjectNavigationEntry} from '../../../../../core/shared/navigation/mvs-object-navigation-entry';
import {
    MvsObjectNavigationActionEnum
} from '../../../../../core/shared/navigation/mvs-object-navigation-action-enum';
import {
    MvsObjectNavigationProviderGeneric
} from '../../../../../core/shared/navigation/impl/mvs-object-navigation-provider-generic';
import {ObjectIdentifier} from '../../../../../core/shared/basic/object-identifier';
import {ObjectIdentifierData} from '../../../../../core/shared/basic/object-identifier-data';
import {FilterCriteria} from '../../../../../core/shared/filter/api/filter.criteria';
import {QlQueryDto} from '../../../../../core/shared/ql/data/dto/ql-query.dto';
import {WidgetData} from '../../../../../core/shared/widget/widget-data';
import {WidgetFactory} from '../../../../../core/shared/widget/service/widget.factory';

/**
 * „Beratungsdokumentation" of the CONSULTANT shell — {@code cw/consultationDocs} there.
 *
 * <p>ONE table over the {@code cw.CwConsultationDoc} rows the logged-on user created, reached from
 * the shell's left icon rail. The standalone (case-less) consultation doc is deliberately NOT indexed,
 * so a browsable list is the only way back to it at all; this page is that way in the consultant
 * shell, the user-scoped counterpart of {@code cw-config}'s unfiltered Beratungsdokumentationen tab
 * on the Alpha side.</p>
 *
 * <h3>Consultant-shell-only surface</h3>
 * Lives under {@code page/consultant-page/} rather than {@code page/overview/} for the same reason
 * as {@code CwMyRequestsPage}: it is routed by {@code shell-consultant} alone. The component is
 * declared AND exported by {@code CwModule}, which the consultant shell already carries via
 * {@code ConsultantCoreModule -> FeatureCrmModule}, so no new module import is needed there.
 *
 * <h3>Read-only, and uncurated by design</h3>
 * No create, no status change, no delete. The table offers EVERY field of the entity and applies no
 * sorting; whoever uses the page narrows and orders it themselves through the widget's own
 * configuration dialog, which persists their choice against this page's alias. A row click opens the
 * document in the shell's right drawer — see {@link handleObjectSelect}.
 *
 * <h3>Scoped to the logged-on user</h3>
 * The ONE thing the page does decide is WHOSE documentation it shows: the single fixed filter binds
 * {@code createdBy} to the logged-on user. This is not a browse-everything surface — it is the
 * consultant's own file. Note that the scope is the record's CREATOR, not its Berater; the inline
 * note at the filter says why, and why that is not freely interchangeable.
 *
 * <p>Structure copied from {@code features/feature-crm/pc/page/overview/pc-me/pc-my-calls/pc-my-calls.page.ts}:
 * the only other overview page that is a widget list whose row click opens the right drawer, and the
 * one that wires {@link MvsObjectNavigationProviderGeneric} correctly.</p>
 */
@Component({
    selector: 'cw-consultation-docs-page',
    templateUrl: './cw-consultation-docs.page.html',
    standalone: false
})
export class CwConsultationDocsPage extends PageComponent implements OnInit {

    /** The single table of this page. Built once in {@link refreshComponent}. */
    widgetConsultationDocs: WidgetData;

    /**
     * Own alias, deliberately NOT {@code config.cw.CwConsultationDoc.widget} (cw-config.page.ts:529).
     *
     * <p>The alias is identity here, not decoration. A stored {@code ui_widget_layout} row is looked
     * up BY ALIAS, and on this page that row is the whole curation mechanism: the user picks their
     * columns in the widget configuration dialog and the choice is saved against this alias. Sharing
     * the config page's alias would mean the admin's column choice and the consultant's overwrite
     * each other, and would additionally let a layout saved there decide which fields this page
     * fetches at all — an absent column, never an error. One surface, one alias; never reuse, and
     * never rename, because renaming silently discards everyone's saved layout.</p>
     */
    static readonly WIDGET_ALIAS = 'cw.consultationDoc.consultant.overview.widget';

    constructor(
        protected override route: ActivatedRoute,
        protected override coreService: MvsCoreService,
        protected navigationService: MvsObjectNavigationService) {
        super(route, coreService);
    }

    override ngOnInit(): void {
        this.defaultLabel = 'Beratungsdokumentation';
        super.ngOnInit();

        this.initComponent();
    }

    /**
     * Initialize Component.
     */
    initComponent(): void {

        // NOT redundant — do NOT "clean this line up". Without it a row click does nothing at all on
        // a cold load: no drawer, no request, no console error.
        //
        // navigateTo() pushes to no subject — navigateToInternal() only rewrites the tid-sbr* query
        // parameters. The drawer's BehaviorSubject is fed by handleParamsInNavigation() inside the
        // route.queryParams subscription that initService() creates, and initService() runs ONLY from
        // setNavigationProvider(), and only while no provider is set yet.
        //
        // The service is providedIn:'root'. So once anything in the session has activated navigation
        // the page works without this line too — which is why its absence never shows in day-to-day
        // use. But NOTHING under projects/shell-consultant/src/ calls it; in this shell the call is
        // reached only through cr-customer-consultant and cm-contract-consultant. The case that
        // breaks is therefore a cold load landing straight here — login plus the first click in the
        // rail, or F5 on the address. For an icon-rail entry that is the likeliest path of all, not
        // an edge case.
        this.navigationService.setNavigationProvider(new MvsObjectNavigationProviderGeneric());

        this.refreshComponent();
    }

    refreshComponent(): void {
        this.widgetConsultationDocs = this.createConsultationDocsWidget();
        this.initialized = true;
    }

    /**
     * The logged-on user's consultation docs — every column, no sorting, one filter.
     *
     * <h3>Nothing is curated in code, on purpose</h3>
     * The widget is handed no code-side layout at all — no curated column list, no per-column
     * headers, no pinned column order, no hidden-field declarations. Narrowing and ordering is the
     * user's job, done at runtime in the widget configuration dialog, which persists the result as a
     * {@code ui_widget_layout} row against {@link WIDGET_ALIAS}. A code-side column list would fight
     * that saved layout rather than seed it.
     *
     * <p><b>The root object deliberately carries NO {@code fields} list.</b> That is the mechanism:
     * QL projects ALL attributes of an object whose {@code fields} is omitted and labels each one
     * from the entity's own {@code @UiFieldInfo} — which is exactly "get all the columns". A
     * hand-maintained list would have to be extended on every new attribute, and the day somebody
     * forgets, the field is silently unavailable in the configuration dialog. The dialog builds its
     * offer from the WIDGET'S FORM, which for a {@code dataSource = 'ql'} widget is this query's QL
     * template — so a field this query does not read cannot be switched on at all.</p>
     *
     * <p>A second consequence: with no stored layout and nothing hidden,
     * {@code mvs-widget.component.ts} sets {@code selectAttributes} to null and the request fetches
     * everything — so the row's {@code id} is always present and the row click always carries a real
     * object id.</p>
     *
     * <h3>Explicit LEFT joins, never {@code resolve_fkey_texts}</h3>
     * Four LEFT joins: the three FK texts (Kunde, Art, Berater) plus the Vorgang, which is a REVERSE
     * join because the FK lives on the case — see the inline note there. They use the shape
     * {@code cw-case-worklist.widget.ts:96-118} uses. The FK-resolving factory is avoided for the
     * reason recorded there: its {@code resolve_fkey_texts} step crashed the frontend enum converter
     * on {@code cw.CwCase} — a stored ordinal exceeding the FE enum length threw
     * ArrayIndexOutOfBounds in {@code TypeConvertorService.convertAnyToEnumValue} and EVERY row was
     * lost. This entity carries three enums ({@code closureStatus}, {@code handoverForm},
     * {@code internalChannelType}); explicit joins have no such step.
     *
     * <p>Every DISPLAY join keeps its own short {@code fields} list. That is not a projection
     * narrowing of the subject — it is the joined entity contributing only the name a column renders;
     * a full projection there would drag ~30 unrelated columns per join into every row for the sake
     * of one name.</p>
     *
     * <h3>Exactly ONE fixed filter, and it is the creator</h3>
     * {@code e.createdBy = ${E:ME_USERNAME}} — the logged-on user sees the documentation they
     * created and nobody else's. See the inline note at the filter for how the variable resolves,
     * for why the creator is not the same person as the Berater, and for the measured reason the
     * {@code agent} FK cannot be filtered on at all.
     *
     * <p>No SECOND filter, in particular no status filter: {@code CwConsultationDocStatusEnum} has
     * exactly two values (DRAFT, CLOSED), so {@code closureStatus IN (DRAFT, CLOSED)} matches every
     * remaining row — a no-op that reads like a business rule and would silently hide any third value
     * the enum later gains. Status is a COLUMN.</p>
     *
     * <p>No sorting either: the sortings list is empty and the backend's natural order applies. The
     * user sorts the column they care about from the table header, and that choice is saved with
     * their layout.</p>
     */
    protected createConsultationDocsWidget(): WidgetData {

        const qlQuery: QlQueryDto = {
            name: CwConsultationDocsPage.WIDGET_ALIAS,
            type: 'view',
            start: {
                name: 'cw.CwConsultationDoc',
                as: 'e',
                type: 'entity',
                // NO `fields` — see the doc above. This is what projects the entity's COMPLETE
                // attribute set and makes every field offerable in the configuration dialog.
                joins: [
                    // cr.Customer renders through calculatedName, the same display field the cw
                    // worklists use. LEFT without exception: customer is still nullable on this
                    // entity (back-fill migration M-1 pending), and an inner join would silently drop
                    // exactly the rows an admin most needs to find.
                    {
                        type: 'entity', name: 'customer', as: 'cust', joinType: 'left',
                        fields: [{name: 'id'}, {name: 'calculatedName'}],
                    },
                    {
                        type: 'entity', name: 'consultationDocType', as: 'ct', joinType: 'left',
                        fields: [{name: 'id'}, {name: 'name'}],
                    },
                    // am.Agent, not um.User — same as the worklist. Also still nullable, also LEFT.
                    {
                        type: 'entity', name: 'agent', as: 'ag', joinType: 'left',
                        fields: [{name: 'id'}, {name: 'name'}],
                    },
                    // The Vorgang this documentation belongs to — a REVERSE join, and it has to be.
                    // The FK sits on the CASE (CwCase.consultationDoc, @OneToOne @JoinColumn
                    // consultation_doc_id, CwCase.java:279-291); CwConsultationDoc has no attribute
                    // pointing back, so it cannot be joined by name. `{OwningEntity}#{fkAttribute}`
                    // is the synthetic join the reverse-join index registers on the target entity
                    // (MetaService.buildReverseJoinIndexForTenant), merged into deriveJoins() and
                    // consumed like any named join — hence type stays 'entity'; the `#` IS the marker
                    // and there is no reverse/mappedBy flag.
                    //
                    // Shaped exactly like the `pay` join of cw-case-worklist.widget.ts:240-246
                    // (cw.CwCasePayment#cwCase) — the same 1:0..1 reverse cardinality, and the one
                    // in-repo reverse join that actually RENDERS TABLE COLUMNS. Plain fields, nothing
                    // else, so the join STACKS into the root execution unit; that is what puts
                    // `c.caseIdentifier` into the root form, which is in turn what makes it offerable
                    // in the widget configuration dialog and fetchable at all.
                    //
                    // ⛔ Do NOT add `onlyFirst` or `fieldNameParent` here. They are the OBJECT-pipe
                    // pattern (bm-customer-billing-account.component.ts, whose result a hardcoded
                    // template reads), and on a TABLE they silently disable the column instead:
                    // `onlyFirst` makes the join NON-STACKABLE (QlNotStackableException,
                    // QlDpEntityJdbcProviderImpl.java:399-403), so it runs in its own execution unit
                    // and its fields land in form.subForms['caseDto'] rather than the root
                    // form.formFields; the dialog's tree builder reads only the root fields, so the
                    // Vorgang node is built empty and then pruned for having no children, and
                    // selectAttributes can never reach c.caseIdentifier. Measured: the join executed
                    // (pipe status 0) and returned ONLY c.consultationDocId — the Vorgangsnummer was
                    // unreachable and unconfigurable.
                    //
                    // `onlyFirst` costs nothing to omit: CwCase.consultationDoc is @OneToOne, so at
                    // most one Vorgang per document and there is no row multiplication to guard
                    // against — which is the only thing that flag buys.
                    //
                    // fields: id carries the relation, caseIdentifier is the Vorgangsnummer
                    // (CwCase.java:110-112). No FK field: `consultationDocId` was needed only while
                    // the join was NON-stacked, where QlPipeProcessorSimple.generateKey glues a child
                    // execution unit to its parent by literal row key. A stacked join has no separate
                    // child record set, so that path is not reached — and the worklist precedent
                    // projects no FK on its reverse join either.
                    //
                    // LEFT, like the others: a documentation without a Vorgang is the normal case
                    // here — the case-less consultation is the whole reason this entity is
                    // standalone — so an inner join would hide exactly those rows.
                    {
                        type: 'entity', name: 'cw.CwCase#consultationDoc', as: 'c', joinType: 'left',
                        fields: [{name: 'id'}, {name: 'caseIdentifier'}],
                    },
                ],
            },
            // Only the logged-on user's own documentation. `${E:ME_USERNAME}` is resolved
            // server-side: TypeConvertorService.convertTypeInternal routes any `${...}` filter value
            // through RuntimeDataProviderService.resolveRuntimeData keyed by the TARGET FIELD's Java
            // type, and UserRuntimeDateProvider answers it for String.class with
            // authenticatedUser.getUmUser().toString() — the `um:<id>:<username>` form the column is
            // actually stored in. The literal string is the whole mechanism; it must never be
            // resolved in the frontend. Same form as „Meine Tickets" in this shell
            // (tm-my-tickets.component.ts:97,127) and as cm-contract.page.ts:757.
            //
            // ⚠ createdBy is the CREATOR of the record, which is deliberately NOT the same person as
            // `agent`, the advising consultant. A documentation a colleague created FOR this user
            // does not appear here, and one this user created for a colleague does. That is the
            // accepted meaning of "my documentation" on this page.
            //
            // ⚠ Do NOT "improve" this back to `e.agent` with `${E:ME}`. Measured against the dev
            // backend, it breaks the ENTIRE query, not just the filter:
            //     QL: Plan Generation Failed.
            //     CoreRuntimeException: No provider found for java.lang.Long
            //     QL: No pipeline found for query, execution cancelled
            // A QL filter on the FK attribute `agent` resolves to its ID COLUMN, typed Long;
            // RuntimeDataProviderService selects a provider by that target type, and
            // AgentRuntimeDateProvider.isRelevant() accepts only Agent.class
            // (AgentRuntimeDateProvider.java:22-24). Nothing is registered for Long, so plan
            // generation fails and the whole pipeline is cancelled. That is why `${E:ME}` has no
            // production call site anywhere: it cannot reach an Agent FK through QL.
            //
            // The `ag` join above STAYS regardless — Berater remains a selectable column. It is
            // simply not what this page filters on.
            fixedFilters: [
                FilterCriteria.create('e.createdBy', FilterCriteria.cOperatorEqual, '${E:ME_USERNAME}')
            ],
            sortings: [],
        };

        /**
         * AI:
         * Status: "in progress"
         * Type: Widget
         * SubType: Runtime
         * Reason: Beratungsdokumentationen (cw.CwConsultationDoc) — the logged-on user's own list
         *   behind the consultant shell's rail entry, scoped by the single fixed filter
         *   e.createdBy = ${E:ME_USERNAME}. QL table on an explicit QlQueryDto with four LEFT joins so Kunde / Art /
         *   Berater and the Vorgangsnummer read as names, the last of them a REVERSE join because the
         *   FK sits on cw.CwCase. Otherwise UNCURATED (all columns, no sorting) so the user selects
         *   their own columns in the widget configuration dialog, which persists them against this
         *   alias. A row click hands the document to the right drawer; nothing is created, changed or
         *   deleted from this surface.
         */
        const widgetData = WidgetFactory.createWidgetTableQl(
            CwConsultationDocsPage.WIDGET_ALIAS,
            'Beratungsdokumentationen',
            qlQuery
        );

        widgetData.noDataText = 'Keine Beratungsdokumentationen vorhanden';

        return widgetData;
    }

    /**
     * Row click — opens the clicked document in the shell's RIGHT DRAWER.
     *
     * <p>{@code navigateTo(entry, 'right')} is deliberate and {@code handleObjectNavigation()} is
     * deliberately NOT used: that method resolves the registry's {@code objectRoute}, and
     * {@code cw.CwConsultationDoc} HAS one ({@code cw/cwConsultationDoc/:id}), so its URL is never
     * empty, the {@code navigateTo} branch behind it is unreachable, and the call always ends in
     * {@code window.open(url, '_self')} — the whole page navigates and this list is lost. Already
     * written up in-repo at {@code cw-case-object-step-consultation-doc.component.ts:2292-2302}.</p>
     *
     * <p>{@code 'side'} is passed explicitly as the mode: this shell sets
     * {@code MVS_DEFAULT_UI_MODE: 'consultant'}, and the mode round-trips through the
     * {@code tid-sbr-mode} query parameter.</p>
     *
     * <p>⚠ The name argument does NOT reach the drawer — {@code navigateTo} never pushes the entry,
     * and {@code handleParamsInNavigation} rebuilds it from the URL with {@code name: null}. Only
     * objectType, objectId, navigationType, action, mode, params and viewType survive. It is passed
     * anyway as the honest argument for the slot; do not expect a caption from it.</p>
     */
    handleObjectSelect(object: ObjectIdentifierData | ObjectIdentifier): void {
        if (!object || object.objectId == null) {
            return;
        }

        const entry = MvsObjectNavigationEntry.createNavigationEntry(
            'cw.CwConsultationDoc',
            object.objectId,
            null,
            'Beratungsdokumentation',
            null,
            null,
            MvsObjectNavigationActionEnum.any,
            'side'
        );

        this.navigationService.navigateTo(entry, 'right');
    }

}
